import type {
    CellContext as CellContextCore,
    CellData,
    Column as ColumnCore,
    ColumnDef as ColumnDefCore,
    ColumnHelper,
    ColumnPinningState,
    Row as RowCore,
    RowData,
    TableFeatures,
    TableOptions as TableOptionsCore,
} from '@tanstack/react-table';
import {
    columnFilteringFeature,
    columnPinningFeature,
    columnSizingFeature,
    createColumnHelper as createColumnHelperCore,
    createFilteredRowModel,
    createSortedRowModel,
    flexRender,
    globalFilteringFeature,
    rowSelectionFeature,
    rowSortingFeature,
    sortFn_alphanumeric,
    sortFn_datetime,
    sortFn_text,
    tableFeatures,
    useTable,
} from '@tanstack/react-table';
import { isUndefined, omitBy } from 'lodash-es';
import type { CSSProperties, ComponentPropsWithRef, ReactElement, ReactNode } from 'react';
import { memo, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import {
    Table,
    TableBody,
    TableCell,
    TableCellEmpty,
    TableCellLoading,
    TableHead,
    TableHeader,
    TableRow,
} from '../primitives/table.tsx';
export const dataTableFeatures = tableFeatures({
    columnFilteringFeature,
    columnPinningFeature,
    columnSizingFeature,
    globalFilteringFeature,
    rowSelectionFeature,
    rowSortingFeature,
    filteredRowModel: createFilteredRowModel(),
    sortedRowModel: createSortedRowModel(),
    sortFns: {
        alphanumeric: sortFn_alphanumeric,
        datetime: sortFn_datetime,
        text: sortFn_text,
    },
});

type DataTableFeatures = typeof dataTableFeatures;

export type ColumnDef<TData extends RowData, TValue extends CellData = CellData> = ColumnDefCore<
    DataTableFeatures,
    TData,
    TValue
>;
export type Column<TData extends RowData, TValue extends CellData = CellData> = ColumnCore<
    DataTableFeatures,
    TData,
    TValue
>;
export type Row<TData extends RowData> = RowCore<DataTableFeatures, TData>;
export type CellContext<TData extends RowData, TValue extends CellData = CellData> = CellContextCore<
    DataTableFeatures,
    TData,
    TValue
>;
type TableOptions<TData extends RowData> = TableOptionsCore<DataTableFeatures, TData>;

export function createColumnHelper<TData extends RowData>(): ColumnHelper<DataTableFeatures, TData> {
    return createColumnHelperCore<DataTableFeatures, TData>();
}

export type {
    CellData,
    ColumnPinningState,
    FilterFnOption,
    OnChangeFn,
    RowData,
    RowSelectionState,
    SortingState,
    TableFeatures,
    Updater,
} from '@tanstack/react-table';

declare module '@tanstack/react-table' {
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions, @typescript-eslint/no-unused-vars -- merging
    interface ColumnMeta<TFeatures extends TableFeatures, TData extends RowData, TValue extends CellData> {
        align?: 'left' | 'right';
        preserveOpacityWhenDimmed?: boolean;
        enabled?: boolean;
        pinned?: 'left' | 'right';
    }
}

// Make hasNextPage required if isFetchingNextPage is provided

type InfiniteScrollProps = {
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    onFetchNextPage: () => void;
};

type SupportedTableOptions<TData extends RowData> = Pick<
    TableOptions<TData>,
    | 'columns'
    | 'defaultColumn'
    | 'enableMultiRowSelection'
    | 'globalFilterFn'
    | 'manualSorting'
    | 'onGlobalFilterChange'
    | 'onRowSelectionChange'
    | 'onSortingChange'
    | 'state'
>;

type BaseDataTableProps<TData extends RowData> = SupportedTableOptions<TData> & {
    data: TData[] | undefined;
    emptyLabel: ReactNode;
    isLoading?: boolean;
    totalRowsCount?: number;
    onRowClick?: (row: Row<TData>) => void;
    disableHeader?: boolean;
    summaryHeader?: Record<string, ReactNode>;
    dimNonSelectedRows?: boolean;
} & ComponentPropsWithRef<'table'>;

export type DataTableProps<TData extends RowData> = BaseDataTableProps<TData> &
    (InfiniteScrollProps | Partial<InfiniteScrollProps>);

const emptyData: [] = [];

export function DataTable<TData extends RowData>({
    disableHeader = false,
    emptyLabel,
    isLoading = false,
    totalRowsCount = Number.POSITIVE_INFINITY,
    onRowClick,
    data,
    isFetchingNextPage,
    onFetchNextPage,
    hasNextPage,
    summaryHeader,
    dimNonSelectedRows = false,
    columns,
    state,
    defaultColumn,
    enableMultiRowSelection,
    globalFilterFn,
    manualSorting,
    onGlobalFilterChange,
    onRowSelectionChange,
    onSortingChange,
    ref,
    ...tableProps
}: DataTableProps<TData>): ReactElement {
    const enabledColumns = useMemo(
        () => columns.filter((column) => column.meta?.enabled !== false),
        [columns],
    );

    const columnPinning = useMemo<ColumnPinningState>(
        () => ({
            start: pinnedColumnIds(enabledColumns, 'left'),
            end: pinnedColumnIds(enabledColumns, 'right'),
        }),
        [enabledColumns],
    );

    const table = useTable<DataTableFeatures, TData>({
        features: dataTableFeatures,
        ...omitBy(
            {
                defaultColumn,
                enableMultiRowSelection,
                globalFilterFn,
                manualSorting,
                onGlobalFilterChange,
                onRowSelectionChange,
                onSortingChange,
            },
            isUndefined,
        ),
        columns: enabledColumns,
        state: { columnPinning, ...omitBy(state, isUndefined) },
        data: data ?? emptyData,
    });

    const leafColumns = (table.getHeaderGroups().at(-1)?.headers ?? []).map((header) => header.column);
    const columnsCount = leafColumns.length;
    const rowCount = table.getRowModel().rows.length || 0;
    const isEmptyTable = rowCount === 0;
    const hasSelectedVisibleRow = table.getSelectedRowModel().rows.length > 0;

    const { ref: lastRowToFetchRef } = useInView({
        threshold: 0.1,
        onChange: (inView) => {
            if (inView && onFetchNextPage && !isFetchingNextPage && hasNextPage) {
                onFetchNextPage();
            }
        },
        skip: isEmptyTable,
    });

    return (
        <Table
            {...tableProps}
            ref={ref}
            style={{ minWidth: isEmptyTable ? undefined : table.getCenterTotalSize() }}
        >
            {!disableHeader && (
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <TableHead
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        style={getCommonPinningStyles(header.column)}
                                        align={
                                            header.column.columnDef.meta?.align === 'right' ? 'right' : 'left'
                                        }
                                    >
                                        {header.isPlaceholder || isLoading || isEmptyTable
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    ))}

                    {/* Summary Header Row */}
                    {summaryHeader && (
                        <TableRow>
                            {leafColumns.map((column) => {
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- legacy code
                                const columnDef = column.columnDef as ColumnDef<any, any> & {
                                    accessorKey?: string;
                                    summary?: (data: unknown) => ReactNode;
                                };
                                const columnKey = columnDef.accessorKey ?? column.id;
                                // Use column summary function if available, otherwise use value from summaryHeader
                                const value: ReactNode =
                                    typeof columnDef.summary === 'function'
                                        ? columnDef.summary(summaryHeader)
                                        : summaryHeader[columnKey];
                                const align = columnDef.meta?.align === 'right' ? 'right' : 'left';

                                return (
                                    <TableHead
                                        key={column.id}
                                        align={align}
                                        style={getCommonPinningStyles(column)}
                                        className="bg-white font-bold text-neutral-950"
                                    >
                                        {value}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    )}
                </TableHeader>
            )}

            <TableBody>
                {!isLoading && !isEmptyTable
                    ? table.getRowModel().rows.map((row, idx, arr) => {
                          const isDimmed =
                              dimNonSelectedRows && hasSelectedVisibleRow && !row.getIsSelected();

                          return (
                              <TableRow
                                  key={row.id}
                                  ref={
                                      // This can improve UX by fetching in the future
                                      idx === arr.length - (totalRowsCount === arr.length ? 1 : 10)
                                          ? lastRowToFetchRef
                                          : undefined
                                  }
                                  data-state={row.getIsSelected() && 'selected'}
                                  data-dimmed={isDimmed ? 'true' : undefined}
                                  onClick={
                                      onRowClick
                                          ? () => {
                                                onRowClick(row);
                                            }
                                          : undefined
                                  }
                              >
                                  {leafColumns.map((column) => {
                                      const cell = row.getAllCellsByColumnId()[column.id];
                                      if (cell === undefined) {
                                          return null;
                                      }
                                      const meta = cell.column.columnDef.meta;
                                      const dimCell = isDimmed && !meta?.preserveOpacityWhenDimmed;
                                      return (
                                          <TableCell
                                              key={cell.id}
                                              data-state={row.getIsSelected() && 'selected'}
                                              className={
                                                  cell.column.getIsPinned()
                                                      ? 'bg-white group-hover:bg-neutral-25'
                                                      : ''
                                              }
                                              style={getCommonPinningStyles(cell.column, dimCell)}
                                              align={meta?.align === 'right' ? 'right' : 'left'}
                                          >
                                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                          </TableCell>
                                      );
                                  })}
                              </TableRow>
                          );
                      })
                    : null}

                {isLoading || isFetchingNextPage ? (
                    <TableCellLoading colSpan={columnsCount} className={isFetchingNextPage ? 'h-32' : ''} />
                ) : null}

                {!isLoading && isEmptyTable ? (
                    <TableCellEmpty colSpan={columnsCount}>{emptyLabel}</TableCellEmpty>
                ) : null}
            </TableBody>
        </Table>
    );
}

export const MemoDataTable = memo(DataTable) as <TData extends RowData>(
    props: DataTableProps<TData>,
) => ReactNode;

function pinnedColumnIds<TData extends RowData>(
    columns: ColumnDef<TData>[],
    side: 'left' | 'right',
): string[] {
    const ids: string[] = [];
    for (const column of columns) {
        if (column.meta?.pinned === side) {
            const id = resolveColumnId(column);
            if (id !== undefined) {
                ids.push(id);
            }
        }
    }
    return ids;
}

function resolveColumnId<TData extends RowData>(column: ColumnDef<TData>): string | undefined {
    if (column.id !== undefined) {
        return column.id;
    }
    if ('accessorKey' in column) {
        return String(column.accessorKey);
    }
    if (typeof column.header === 'string') {
        return column.header;
    }
    return undefined;
}

function getCommonPinningStyles<TData extends RowData>(
    column: Column<TData>,
    isDimmed = false,
): CSSProperties {
    const isPinned = column.getIsPinned();
    let opacity = 1;
    if (isPinned) {
        opacity = 0.95;
    }
    if (isDimmed) {
        opacity = 0.5;
    }
    return {
        left: isPinned === 'start' ? `${column.getStart('start')}px` : undefined,
        right: isPinned === 'end' ? `${column.getAfter('end')}px` : undefined,
        opacity,
        position: isPinned ? 'sticky' : 'relative',
        width: column.getSize(),
        zIndex: isPinned ? 1 : 0,
    };
}
